import React, { useCallback, useMemo, useState } from 'react';
import {
  ArrowDownLeft,
  ArrowUpRight,
  History,
  RotateCcw,
  Search,
  SlidersHorizontal,
} from 'lucide-react';
import AppLoader from '../../components/modules/AppLoader';
import ModuleAlert from '../../components/modules/ModuleAlert';
import ModulePageHeader from '../../components/modules/ModulePageHeader';
import apiService from '../../utils/apiService';
import { useFetchOnceByKey } from '../../hooks/useMountOnce';
import { formatPts } from '../../utils/moduleHelpers';
import '../../styles/module-shared.css';
import './loyalty-module.css';

const PAGE_SIZE = 10;

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const activityTone = (activity) => {
  const key = String(activity || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
  if (key.includes('redeem')) return 'rose';
  if (key.includes('promot')) return 'amber';
  if (key.includes('refer')) return 'teal';
  if (key.includes('deposit')) return 'blue';
  if (key.includes('trad')) return 'violet';
  if (key.includes('verif') || key.includes('creat') || key.includes('account')) return 'orange';
  if (key.includes('transfer')) return 'indigo';
  return 'slate';
};

const LoyaltyHistory = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);

  const [searchDraft, setSearchDraft] = useState('');
  const [activityDraft, setActivityDraft] = useState('all');
  const [typeDraft, setTypeDraft] = useState('all');

  const [search, setSearch] = useState('');
  const [activity, setActivity] = useState('all');
  const [type, setType] = useState('all');
  const [page, setPage] = useState(1);

  const fetchData = useCallback(async ({ cancelled }) => {
    setLoading(true);
    setError('');
    try {
      const res = await apiService.getLoyaltyPointsHistory({
        pageNumber: 1,
        pageSize: 500,
      });
      if (cancelled()) return;
      if (!res.success) {
        setError(res.error || 'Failed to load loyalty history');
        setRows([]);
        return;
      }
      setRows(Array.isArray(res.data?.history) ? res.data.history : []);
    } catch (err) {
      if (!cancelled()) {
        setError(err.message || 'Failed to load loyalty history');
        setRows([]);
      }
    } finally {
      if (!cancelled()) setLoading(false);
    }
  }, []);

  useFetchOnceByKey('loyalty-history-items-v2', fetchData, true);

  const activityOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((row) => {
      const a = row.activity || row.loyaltyEventName;
      if (a) set.add(String(a));
    });
    return ['all', ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [rows]);

  const stats = useMemo(() => {
    let earned = 0;
    let redeemed = 0;
    rows.forEach((row) => {
      const pts = Number(row.points ?? 0);
      if (pts > 0) earned += pts;
      else if (pts < 0) redeemed += Math.abs(pts);
    });
    return {
      transactions: rows.length,
      earned,
      redeemed,
    };
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((row) => {
      const pts = Number(row.points ?? 0);
      const rowActivity = String(row.activity || row.loyaltyEventName || '');
      const rowType = String(row.type || (pts >= 0 ? 'Earned' : 'Redeemed'));
      const desc = String(row.description || '');

      if (activity !== 'all' && rowActivity.toLowerCase() !== activity.toLowerCase()) {
        return false;
      }
      if (type === 'Earned' && !(rowType.toLowerCase().includes('earn') || pts > 0)) return false;
      if (type === 'Redeemed' && !(rowType.toLowerCase().includes('redeem') || pts < 0)) {
        return false;
      }
      if (q) {
        const blob = `${desc} ${rowActivity} ${rowType}`.toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });
  }, [rows, search, activity, type]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE) || 1);
  const currentPage = Math.min(page, totalPages);
  const pageRows = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, currentPage]);

  const showingFrom = filtered.length ? (currentPage - 1) * PAGE_SIZE + 1 : 0;
  const showingTo = Math.min(currentPage * PAGE_SIZE, filtered.length);

  const applyFilters = () => {
    setSearch(searchDraft);
    setActivity(activityDraft);
    setType(typeDraft);
    setPage(1);
  };

  const resetFilters = () => {
    setSearchDraft('');
    setActivityDraft('all');
    setTypeDraft('all');
    setSearch('');
    setActivity('all');
    setType('all');
    setPage(1);
  };

  return (
    <div className="page lp-page lh-page">
      <div className="page-inner">
        <ModulePageHeader
          icon={History}
          title="Loyalty History"
          subtitle="Review your award history with filters and pagination."
          iconBg="linear-gradient(135deg,#6b0000,#8b0000)"
        />
        <ModuleAlert message={error} />

        <section className="lh-hero">
          <div className="lh-hero__copy">
            <span className="lh-hero__eyebrow">Activity Log</span>
            <h2>Your points journey</h2>
            <p>Track every earn, redeem, and bonus across your loyalty account.</p>
          </div>
          <div className="lh-hero__stats">
            <div className="lh-hero__stat">
              <strong>{formatPts(stats.transactions)}</strong>
              <span>Transactions</span>
            </div>
            <div className="lh-hero__stat is-earn">
              <strong>
                <ArrowUpRight className="w-4 h-4" />
                +{formatPts(stats.earned)}
              </strong>
              <span>Earned</span>
            </div>
            <div className="lh-hero__stat is-redeem">
              <strong>
                <ArrowDownLeft className="w-4 h-4" />
                -{formatPts(stats.redeemed)}
              </strong>
              <span>Redeemed</span>
            </div>
          </div>
        </section>

        <section className="lh-filters">
          <div className="lh-filters__head">
            <SlidersHorizontal className="w-4 h-4" />
            <h3>Filter records</h3>
          </div>

          <div className="lh-filters__grid">
            <label className="lh-field lh-field--search">
              <span className="lh-field__label">Search</span>
              <div className="lh-field__control">
                <Search className="w-4 h-4" />
                <input
                  type="search"
                  value={searchDraft}
                  onChange={(e) => setSearchDraft(e.target.value)}
                  placeholder="Search description or activity"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') applyFilters();
                  }}
                />
              </div>
            </label>

            <label className="lh-field">
              <span className="lh-field__label">Activity</span>
              <select
                value={activityDraft}
                onChange={(e) => setActivityDraft(e.target.value)}
                aria-label="Filter by activity"
              >
                {activityOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt === 'all' ? 'All activities' : opt}
                  </option>
                ))}
              </select>
            </label>

            <label className="lh-field">
              <span className="lh-field__label">Type</span>
              <select
                value={typeDraft}
                onChange={(e) => setTypeDraft(e.target.value)}
                aria-label="Filter by type"
              >
                <option value="all">All types</option>
                <option value="Earned">Earned</option>
                <option value="Redeemed">Redeemed</option>
              </select>
            </label>

            <div className="lh-filters__actions">
              <button type="button" className="lh-btn lh-btn--ghost" onClick={resetFilters}>
                <RotateCcw className="w-3.5 h-3.5" />
                Reset
              </button>
              <button type="button" className="lh-btn lh-btn--primary" onClick={applyFilters}>
                Apply Filters
              </button>
            </div>
          </div>
        </section>

        <section className="lh-table-card">
          <div className="lh-table-card__head">
            <div>
              <h3>Award History</h3>
              <p>Detailed log of loyalty point activity</p>
            </div>
            <span className="lh-table-card__count">{filtered.length} records</span>
          </div>

          {loading ? (
            <div className="lh-table-card__loading">
              <AppLoader color="#8b0000" />
            </div>
          ) : pageRows.length === 0 ? (
            <div className="lh-table-card__empty">
              <History className="w-10 h-10 mx-auto mb-3 text-gray-300" />
              <p>No loyalty history found for the selected filters.</p>
            </div>
          ) : (
            <>
              <div className="lh-table-wrap">
                <table className="lh-table">
                  <thead>
                    <tr>
                      <th>Date &amp; Time</th>
                      <th>Activity</th>
                      <th>Description</th>
                      <th>Points</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((row, index) => {
                      const delta = Number(row.points ?? 0);
                      const positive = delta >= 0;
                      const activity = row.activity || row.loyaltyEventName || 'Activity';
                      return (
                        <tr key={row.id || index}>
                          <td className="lh-table__date">
                            {formatDateTime(row.date || row.createdDateTime)}
                          </td>
                          <td>
                            <span className={`lh-activity tone-${activityTone(activity)}`}>
                              {activity}
                            </span>
                          </td>
                          <td className="lh-table__desc">
                            {row.description || 'Loyalty activity'}
                          </td>
                          <td>
                            <span className={`lh-points${positive ? ' is-pos' : ' is-neg'}`}>
                              {positive ? (
                                <ArrowUpRight className="w-3.5 h-3.5" />
                              ) : (
                                <ArrowDownLeft className="w-3.5 h-3.5" />
                              )}
                              {positive ? '+' : ''}
                              {formatPts(delta)} PTS
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="lh-pager">
                <span>
                  Showing {showingFrom}–{showingTo} of {filtered.length}
                </span>
                <div className="lh-pager__controls">
                  <button
                    type="button"
                    className="lh-btn lh-btn--ghost"
                    disabled={currentPage <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </button>
                  <span>
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    type="button"
                    className="lh-btn lh-btn--ghost"
                    disabled={currentPage >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
};

export default LoyaltyHistory;
